import { store } from './state/store.js';

export interface Route {
  path: string; // e.g. '/login' or '/u/:id'
  requireAuth?: boolean;
  render: (root: HTMLElement, params?: Record<string, string>) => void | Promise<void>;
}

let routes: Route[] = [];
let rootEl: HTMLElement | null = null;

function currentPath(): string {
  const h = window.location.hash || '#/';
  return h.startsWith('#') ? h.slice(1) : h;
}

/** Strip ?query and #fragment so matching is path-only. */
function pathOnly(p: string): string {
  const q = p.indexOf('?');
  if (q >= 0) p = p.slice(0, q);
  const f = p.indexOf('#');
  if (f >= 0) p = p.slice(0, f);
  return p || '/';
}

/** Match a path against the route table, supporting `:param` segments
 *  (e.g. '/u/:id'). Returns the matched route plus extracted params. */
function matchRoute(path: string): { route: Route; params: Record<string, string> } | undefined {
  const pathSegs = pathOnly(path).split('/').filter(Boolean);
  for (const r of routes) {
    const routeSegs = r.path.split('/').filter(Boolean);
    if (routeSegs.length !== pathSegs.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < routeSegs.length; i++) {
      const rs = routeSegs[i]!;
      const ps = pathSegs[i]!;
      if (rs.startsWith(':')) {
        params[rs.slice(1)] = decodeURIComponent(ps);
      } else if (rs !== ps) {
        ok = false;
        break;
      }
    }
    if (ok) return { route: r, params };
  }
  return undefined;
}

async function render(): Promise<void> {
  if (!rootEl) return;
  const path = currentPath();

  const matched = matchRoute(path);
  if (!matched) {
    // Unknown path — fall back.
    navigate(store.get().accessToken ? '/profile' : '/login');
    return;
  }
  const { route, params } = matched;

  if (route.requireAuth && !store.get().accessToken) {
    navigate('/login');
    return;
  }
  const base = pathOnly(path);
  if (!route.requireAuth && store.get().accessToken && (base === '/login' || base === '/register' || base === '/')) {
    navigate('/profile');
    return;
  }

  rootEl.innerHTML = '';
  // Every navigation starts at the top of the page. Without this, the browser
  // keeps the previous page's scroll offset across the hash change, and the new
  // page's initial anchored(renderMain) then "restores" that stale offset — so
  // a freshly opened profile or page can appear scrolled partway down.
  const scroller = document.scrollingElement ?? document.documentElement;
  scroller.scrollTop = 0;
  scroller.scrollLeft = 0;
  // Loading state: pages that fetch before painting used to leave nothing but
  // bare wallpaper while their data was in flight — on a slow link the realm
  // looked broken for a second or three on every navigation. Show a quiet
  // in-voice marker, but only once the wait is long enough to be noticed
  // (fast renders never see it, so there's no flash).
  const loading = document.createElement('div');
  loading.setAttribute('aria-busy', 'true');
  loading.style.cssText =
    'display:flex; flex-direction:column; align-items:center; justify-content:center;' +
    ' gap:10px; min-height:60vh; color:var(--c-ink-soft, #6b5a40);' +
    ' font-family: var(--font-elegant, Georgia, serif); font-style: italic; font-size: 1.05rem;';
  loading.innerHTML =
    '<span style="font-size:2rem; color:var(--c-gold, #b08d2f); animation: rm-route-pulse 1.2s ease-in-out infinite;">⚜</span>' +
    '<span>The page turns…</span>' +
    '<style>@keyframes rm-route-pulse { 0%,100% { opacity: 0.45; } 50% { opacity: 1; } }</style>';
  const showLoading = window.setTimeout(() => {
    // Only if the page hasn't painted anything of its own yet.
    if (rootEl && rootEl.childElementCount === 0) rootEl.append(loading);
  }, 150);
  try {
    await route.render(rootEl, params);
  } finally {
    window.clearTimeout(showLoading);
    loading.remove();
  }
}

export function navigate(path: string): void {
  const target = `#${path}`;
  if (window.location.hash === target) {
    void render();
  } else {
    window.location.hash = target;
  }
}

export function startRouter(root: HTMLElement, defs: Route[]): void {
  rootEl = root;
  routes = defs;
  window.addEventListener('hashchange', () => void render());
  void render();
}
